import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBooleanAnswerOptions } from '@app/constants';
import { translationsToFormValues } from '@app/utils';
import { useQuestionDetails, useQuestionOptions } from '@data/hooks';
import { AnswerType, Language } from '@data/sdk/match';
import { AntdForm } from '@ui/components';
import { BooleanDependencyFormValues } from '../types';

/**
 * Tracks the selected source question's answer type and, for an options source, its
 * translated option list — so the applicable-answer field can switch between the fixed
 * boolean options and the source's own options.
 */
export const useDependencySource = (
  form: ReturnType<typeof AntdForm.useForm<BooleanDependencyFormValues>>[0],
  isEditing: boolean,
) => {
  const { i18n } = useTranslation();
  const [sourceAnswerType, setSourceAnswerType] = useState<AnswerType | undefined>(undefined);
  const isOptionsSource = sourceAnswerType === AnswerType.Options;

  const sourceId: string | undefined = AntdForm.useWatch(['source', 'value'], form);

  // Editing an existing dependency: the source select is disabled, so `onSourceSelect`
  // never fires — resolve the answerType by id instead of relying on the paginated list.
  const { data: editedSourceQuestion } = useQuestionDetails(isEditing ? sourceId ?? '' : '');
  useEffect(() => {
    if (editedSourceQuestion) setSourceAnswerType(editedSourceQuestion.answerType);
  }, [editedSourceQuestion]);

  const { data: sourceOptions, isLoading: sourceOptionsLoading } = useQuestionOptions(
    sourceId ?? '',
    { limit: 50, offset: 0 },
    isOptionsSource,
  );

  const sourceAnswerOptions = useMemo(
    () =>
      (sourceOptions?.items ?? []).map((option) => {
        const translations = translationsToFormValues(option.translations);
        return { label: translations[i18n.language as Language] || option.key, value: option.key };
      }),
    [sourceOptions, i18n.language],
  );

  const applicableAnswerOptions = isOptionsSource ? sourceAnswerOptions : getBooleanAnswerOptions();

  const onSourceSelect = (_: unknown, option: { answerType: AnswerType }) => {
    setSourceAnswerType(option.answerType);
    form.resetFields(['applicableAnswer', 'notApplicableAnswer']);
  };

  return {
    isOptionsSource,
    sourceOptionsLoading,
    applicableAnswerOptions,
    onSourceSelect,
  };
};
